import { useSyncExternalStore } from 'react';
import config from '../utils/config';
import { getLocalStorage, getUser } from '../utils/func';
import { isStorageKey } from '../utils/storage';

// A user can sign out/in, including as the same account, in another tab while
// a private screen or a device prompt is still open. Own async work by both
// the account and its token; profile edits alone must not restart that screen.
export const getSessionIdentity = () => JSON.stringify([
  getUser()?.id ?? null, getLocalStorage(config.token) || '',
]);

export const subscribeSessionChanges = notify => {
  const storage = event => {
    if (isStorageKey(event, config.token) || isStorageKey(event, config.user)) notify();
  };
  // Android TWA and browser tabs share a session. A suspended/BFCache page
  // may resume before queued storage events; always reconcile on foreground.
  // Stable snapshots make this a no-op for unchanged accounts and drafts.
  const foreground = () => {
    if (document.visibilityState !== 'hidden') notify();
  };
  window.addEventListener('wiw:session-changed', notify);
  window.addEventListener('storage', storage);
  window.addEventListener('pageshow', foreground);
  window.addEventListener('focus', foreground);
  document.addEventListener('visibilitychange', foreground);
  return () => {
    window.removeEventListener('wiw:session-changed', notify);
    window.removeEventListener('storage', storage);
    window.removeEventListener('pageshow', foreground);
    window.removeEventListener('focus', foreground);
    document.removeEventListener('visibilitychange', foreground);
  };
};

export default function useSessionIdentity() {
  return useSyncExternalStore(subscribeSessionChanges, getSessionIdentity, getSessionIdentity);
}

// Footer identity also follows name/photo edits. Keep a primitive snapshot so
// useSyncExternalStore does not mistake JSON parsing for a new account change.
const userSnapshot = () => JSON.stringify(getUser() || null);
export function useSessionUser() {
  return JSON.parse(useSyncExternalStore(subscribeSessionChanges, userSnapshot, userSnapshot));
}
